import { ArrowRight, CheckCircle } from 'flowbite-react-icons/outline'
import { Button } from '@helix-ds/react'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthorization } from '@/features/authorization/authorizationHooks'
import { homePathForPermissions } from '@/features/auth/homeRouting'

/**
 * First-time user welcome page
 * Shows when user has never logged in before (last_login === null in Auth0)
 * Implements design from Figma (node-id=387-6691)
 * @returns {JSX.Element} The first-time welcome component
 */
const FirstTimeWelcome: React.FC = () => {
  const navigate = useNavigate()
  const { permissions } = useAuthorization()

  const handleGetStarted = () => {
    // Route to the permission-aware home: org-less case-only users go to /cases
    // (their only surface), everyone with order access goes to /orders.
    navigate(homePathForPermissions(permissions))
  }

  return (
    <div className="relative flex h-screen flex-col items-center justify-center overflow-y-auto bg-background-secondary-medium dark:bg-gray-800 py-8">
      {/* Logo */}
      <div className="mb-12 flex w-full max-w-[610px] justify-center px-8">
        <img
          src="/icons/Inflection_Logo_Dark_LG.png"
          alt="Inflection Medicine"
          className="h-14 w-auto"
        />
      </div>

      {/* Welcome Card */}
      <div className="relative z-10 w-full max-w-[610px] px-8">
        <div className="rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 p-8 shadow-2xl">
          {/* Welcome Icon */}
          <div className="mb-6 flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-50 dark:bg-gray-800">
              <CheckCircle className="h-8 w-8 text-brand-600 dark:text-brand-400" />
            </div>
          </div>

          {/* Welcome Text */}
          <h1 className="mb-3 text-center text-2xl font-semibold leading-8 tracking-tight text-text-heading dark:text-white">
            Welcome to Inflection Medicine
          </h1>

          <p className="mb-8 text-center text-base leading-6 text-text-body dark:text-gray-400">
            Your account has been created successfully. Get started by placing your first order.
          </p>

          {/* Get Started Button */}
          <Button
            type="button"
            variant="primary"
            size="lg"
            fullWidth
            onClick={handleGetStarted}
            className="focus-visible:ring-brand-600"
          >
            <span>Get Started</span>
            <ArrowRight className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* DNA Wave Pattern at Bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-[503px] overflow-hidden">
        <img
          src="/icons/INFL Wave B 2026 - on dark.svg"
          alt=""
          className="h-full w-full object-cover"
          aria-hidden="true"
        />
      </div>
    </div>
  )
}

export default FirstTimeWelcome
